import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TaskStatusChip from '../utils/common/TaskStatusChip.jsx';
import Note from './Note';

// Helpers functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray?.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

// Constants
const DEFAULT_ORDER = 'desc';
const DEFAULT_ORDER_BY = '_id';

const headers = [
  {
    id: 'ticket',
    align: 'left',
    label: 'Référence Ticket',
  },
  {
    id: 'client',
    align: 'left',
    label: 'Client',
  },
  {
    id: 'status',
    align: 'left',
    label: 'Status',
  },
  {
    id: 'assignedTo',
    align: 'left',
    label: 'Assigné à',
  },
  {
    id: 'type',
    align: 'left',
    label: 'Type de tâche',
  },
  {
    id: 'notes',
    align: 'left',
    label: 'Notes',
  },
];

const InboxTasksTableToolbar = ({
  loading,
  validating,
  nb,
  inboxData,
  setIsDone,
  setAssignedTypeTask,
}) => {
  const handleIsDoneCheckbox = useCallback(() => {
    setIsDone(!inboxData?.isDone);
  }, [setIsDone, inboxData?.isDone]);

  const handleAssignedTypeTaskCheckbox = useCallback(() => {
    if (inboxData?.assignedTypeTask === true) {
      setAssignedTypeTask(false); // Passe à "Créées par moi"
    } else if (inboxData?.assignedTypeTask === false) {
      setAssignedTypeTask(null); // Passe à "Liées à moi"
    } else {
      setAssignedTypeTask(true); // Passe à "Assignées à moi"
    }
  }, [setAssignedTypeTask, inboxData?.assignedTypeTask]);

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography
        component="span"
        sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 1 }}
        variant="h6"
        id="tableTitle"
      >
        Tâches à traiter :{' '}
        {loading || validating ? (
          <CircularProgress color="info" size="0.8em" />
        ) : (
          nb
        )}
      </Typography>

      {/* Filtres */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormGroup row sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {inboxData?.isDone !== undefined && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={inboxData?.isDone}
                  onChange={handleIsDoneCheckbox}
                  aria-label="Terminé"
                />
              }
              label="Terminé"
            />
          )}

          {inboxData?.assignedTypeTask !== undefined && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={inboxData?.assignedTypeTask === true}
                  indeterminate={inboxData?.assignedTypeTask === null}
                  onChange={handleAssignedTypeTaskCheckbox}
                  aria-label="Filtre d'assignation"
                />
              }
              label={
                inboxData?.assignedTypeTask === true
                  ? 'Assignées à moi'
                  : inboxData?.assignedTypeTask === false
                    ? 'Créées par moi'
                    : 'Liées à moi'
              }
            />
          )}
        </FormGroup>
      </Box>
    </Toolbar>
  );
};

const InboxTasksTableHead = (props) => {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (newOrderBy) => (event) => {
    onRequestSort(event, newOrderBy);
  };

  return (
    <TableHead>
      <TableRow>
        {headers?.map((header) => (
          <TableCell
            key={header?.id}
            align={header?.align}
            sortDirection={orderBy === header?.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === header?.id}
              direction={orderBy === header?.id ? order : 'asc'}
              onClick={createSortHandler(header?.id)}
              sx={{ fontWeight: 'bold' }}
            >
              {header?.label}
              {orderBy === header?.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const InboxTaskRow = ({ row, headers }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const profile = useSelector((state) => state?.login?.profile);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!row?.ticket?.reference) {
    return (
      <TableRow hover tabIndex={-1} sx={{ cursor: 'pointer' }} value={row?._id}>
        {headers?.map((h) => (
          <TableCell key={`loading-${h.id}`}>
            <Skeleton variant="text" sx={{ fontSize: '2em' }} />
          </TableCell>
        ))}
      </TableRow>
    );
  }

  const lastNote = row?.notes?.[0] || null;

  const handleExpandRow = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleNavigateToProject = useCallback(() => {
    navigate(
      `/project/${row?.ticket?.project?._id}/tickets/${row?.ticket?._id}/tasks/${row?._id}`
    );
  }, [navigate, row]);

  const getPlannedDate = (date) => {
    if (!date) {
      return null;
    }
    const plannedDate = new Date(date);
    return plannedDate.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const backgroundColor =
    lastNote?.user?._id !== profile?._id
      ? theme.palette.info.light
      : 'transparent';

  return (
    <TableRow
      hover
      tabIndex={-1}
      sx={{ cursor: 'pointer', backgroundColor }}
      value={row?._id}
    >
      <TableCell sx={{ width: '10%' }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleNavigateToProject}
          sx={{ margin: 0, whiteSpace: 'nowrap', minWidth: 'auto' }}
        >
          {row?.ticket?.reference}
        </Button>
      </TableCell>
      <TableCell sx={{ width: '10%' }}>
        {row?.ticket?.contact?.firstName} {row?.ticket?.contact?.lastName}
      </TableCell>
      <TableCell sx={{ width: '10%' }}>
        <TaskStatusChip status={row?.status} />
        {row?.scheduledAt && (
          <Typography
            sx={{
              fontSize: '0.9em',
              fontStyle: 'italic',
              color: theme.palette.text.secondary,
              marginTop: '0.5em',
            }}
          >
            {getPlannedDate(row?.scheduledAt)}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ width: '10%' }}>
        {row?.assignedTo?.firstName} {row?.assignedTo?.lastName}
      </TableCell>
      <TableCell sx={{ width: '15%' }}>{row?.action?.name}</TableCell>
      <TableCell>
        <Box>
          <Accordion expanded={isExpanded} onChange={handleExpandRow}>
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon sx={{ fill: theme.palette.text.primary }} />
              }
            >
              <Typography
                component={'span'}
                sx={{ minWidth: '80%', fontSize: '1em' }}
              >
                <Note isLastNote note={lastNote} expanded={isExpanded} />
              </Typography>
              <Typography
                sx={{
                  minWidth: '20%',
                  fontSize: '1em',
                  textAlign: 'end',
                  fontStyle: 'italic',
                  paddingRight: '1em',
                  paddingTop: '0.15em',
                }}
              >
                {row?.notes?.length
                  ? `${row.notes.length} Note(s)`
                  : 'Aucune note'}
              </Typography>
            </AccordionSummary>
            {row?.notes?.slice(1).map((taskNote) => (
              <Fragment key={taskNote?._id}>
                <Divider />
                <AccordionDetails>
                  <Note note={taskNote} expanded={isExpanded} />
                </AccordionDetails>
              </Fragment>
            ))}
            <Divider />
          </Accordion>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const InboxTaskTable = ({
  loading,
  validating,
  datas,
  inboxData,
  limit,
  setLimit,
  page,
  setPage,
  setIsDone,
  setAssignedTypeTask,
  total,
  _mutate,
}) => {
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderedRows, setOrderedRows] = useState([]);

  useEffect(() => {
    setOrderedRows(
      stableSort(datas, getComparator(DEFAULT_ORDER, DEFAULT_ORDER_BY))
    );
  }, [datas]);

  const handleRequestSort = useCallback(
    (event, newOrderBy) => {
      const isAsc = orderBy === newOrderBy && order === 'asc';
      const toggledOrder = isAsc ? 'desc' : 'asc';
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
      setOrderedRows(
        stableSort(datas, getComparator(toggledOrder, newOrderBy))
      );
    },
    [order, orderBy, datas]
  );

  const handleChangePage = useCallback(
    (event, newPage) => {
      setPage(newPage);
      setOrderedRows(stableSort(datas, getComparator(order, orderBy)));
    },
    [order, orderBy, datas]
  );

  const handleChangeLimit = useCallback(
    (event) => {
      setLimit(parseInt(event.target.value, 10));
      setPage(0);
      setOrderedRows(stableSort(datas, getComparator(order, orderBy)));
    },
    [order, orderBy, datas]
  );

  return (
    <Paper elevation={24} sx={{ width: '100%', mb: 2 }}>
      <InboxTasksTableToolbar
        loading={loading}
        validating={validating}
        nb={total}
        inboxData={inboxData}
        setIsDone={setIsDone}
        setAssignedTypeTask={setAssignedTypeTask}
      />
      <TableContainer>
        <Table sx={{ minWidth: 750 }} size="small">
          <InboxTasksTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {loading ? (
              <CircularProgress />
            ) : (
              orderedRows?.map((row) => (
                <InboxTaskRow key={row._id} row={row} headers={headers} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {validating && <CircularProgress color="info" size="1.3em" />}
        <TablePagination
          rowsPerPageOptions={[10]}
          component="div"
          count={total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeLimit}
        />
      </Box>
    </Paper>
  );
};

export default InboxTaskTable;
